// src/pages/Debts.js
//
// Debt strategy & amortization page.
// • Lists all active debts
// • Shows portfolio summary (total balance, weighted rate, projected payoff)
// • Strategy planner: avalanche vs snowball, total monthly budget slider
// • Per-debt cards with what-if input
// • Visual chart of total balance over time
// • Editable payoff schedule table with manual overrides
//
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useOutletContext, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useDataEvent, emitDataEvent, EVENTS } from '../lib/dataEvents'
import { recordDebtBalance } from '../lib/debtHistory'
import {
  calculatePayoff, runStrategy, formatMonths, monthsFromNow, computeAccruedInterest,
} from '../utils/amortization'
import { theme } from '../lib/theme'

const c = theme.colors
const f = theme.fonts

const formatCurrency = (n) => {
  if (!isFinite(n)) return '—'
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}
const formatCurrencyDetailed = (n) => {
  if (!isFinite(n)) return '—'
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}
const formatDate = (date) => {
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}
const formatShortDate = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
const DEBT_TYPES = [
  { value: 'credit_card',   label: 'Credit Card' },
  { value: 'student_loan',  label: 'Student Loan' },
  { value: 'auto_loan',     label: 'Auto Loan' },
  { value: 'mortgage',      label: 'Mortgage' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'medical',       label: 'Medical' },
  { value: 'other',         label: 'Other' },
]

export default function Debts() {
  const ctx = useOutletContext() || {}
  const [debts, setDebts] = useState([])
  const [loading, setLoading] = useState(true)
  const [strategy, setStrategy] = useState('avalanche')
  const [extraBudget, setExtraBudget] = useState(0)
  const [perDebtExtras, setPerDebtExtras] = useState({})
  const [showHowItWorks, setShowHowItWorks] = useState(false)
  const [overrides, setOverrides] = useState({})
  // Chunk 6 — CRUD + interest accrual
  const [partnerId, setPartnerId] = useState(null)
  const [activeForm, setActiveForm] = useState(null) // null | 'add' | { debt }
  const [accruals, setAccruals] = useState({})       // debtId → { amount, since } surfaced this session
  const accrualRan = useRef(false)                    // accrue at most once per mount (guards StrictMode/refetch)

  const handleOverride = (month, debtId, amount) => {
    setOverrides(prev => {
      const monthOverrides = { ...(prev[month] || {}) }
      const num = parseFloat(amount)
      if (!num || num <= 0) {
        delete monthOverrides[debtId]
      } else {
        monthOverrides[debtId] = num
      }
      const next = { ...prev, [month]: monthOverrides }
      if (Object.keys(monthOverrides).length === 0) delete next[month]
      return next
    })
  }

  useEffect(() => { fetchAll() }, [])
  // Live refresh when a debt changes elsewhere (e.g. an auto-matched payment
  // from Log/QuickAdd). Accrual is ref-guarded so this never double-accrues.
  useDataEvent([EVENTS.DEBT_SAVED, EVENTS.DEBT_UPDATED, EVENTS.DEBT_DELETED], () => fetchAll())

  async function fetchAll() {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }

    const { data: link } = await supabase.from('partner_links')
      .select('user_id_1, user_id_2')
      .or(`user_id_1.eq.${user.id},user_id_2.eq.${user.id}`)
      .eq('is_active', true).maybeSingle()
    const pid = link
      ? (link.user_id_1 === user.id ? link.user_id_2 : link.user_id_1)
      : null
    setPartnerId(pid)
    const userIds = [user.id, ...(pid ? [pid] : [])]

    const { data: debtsData } = await supabase.from('debts')
      .select('*')
      .in('user_id', userIds)

    let list = debtsData || []
    // Interest accrual on open — once per mount (ref set synchronously before any
    // await, so a concurrent StrictMode re-invoke can't double-accrue).
    if (!accrualRan.current) {
      accrualRan.current = true
      const { updated, surfaced } = await accrueTrackedDebts(list)
      list = updated
      setAccruals(surfaced)
    }
    setDebts(list)
    setLoading(false)
  }

  // Accrue simple daily interest for tracked-rate debts (is_simple_mode === false).
  // Baseline case (last_interest_accrual_at IS NULL): stamp now WITHOUT accruing,
  // so interest already baked into today's balance isn't double-counted. Returns
  // the updated debt list (fresh balances) and a { debtId → { amount, since } }
  // map of accruals to surface in the UI this session only.
  async function accrueTrackedDebts(list) {
    const now = new Date()
    const nowIso = now.toISOString()
    const surfaced = {}
    const updated = []
    for (const d of list) {
      if (d.is_simple_mode !== false) { updated.push(d); continue } // simple-mode → skip
      if (!d.last_interest_accrual_at) {
        // Baseline: set the clock, accrue nothing.
        await supabase.from('debts').update({ last_interest_accrual_at: nowIso }).eq('id', d.id)
        updated.push({ ...d, last_interest_accrual_at: nowIso })
        continue
      }
      const { accrued } = computeAccruedInterest(d.current_balance, d.interest_rate, d.last_interest_accrual_at, now)
      if (accrued > 0.005) {
        const newBalance = parseFloat(d.current_balance || 0) + accrued
        await supabase.from('debts').update({
          current_balance: newBalance,
          last_interest_accrual_at: nowIso,
          updated_at: nowIso,
        }).eq('id', d.id)
        await recordDebtBalance(d.id, newBalance, 'interest_accrual', nowIso)
        surfaced[d.id] = { amount: accrued, since: d.last_interest_accrual_at }
        updated.push({ ...d, current_balance: newBalance, last_interest_accrual_at: nowIso })
      } else {
        updated.push(d)
      }
    }
    return { updated, surfaced }
  }

  const reloadDebts = () => fetchAll()

  const totalMinPayment = useMemo(() => {
    return debts.reduce((s, d) => s + parseFloat(d.monthly_payment || 0), 0)
  }, [debts])

  const totalBudget = useMemo(() => {
    const perDebtTotal = Object.values(perDebtExtras).reduce((s, n) => s + (parseFloat(n) || 0), 0)
    return totalMinPayment + extraBudget + perDebtTotal
  }, [totalMinPayment, extraBudget, perDebtExtras])

  const totalBalance = useMemo(() => {
    return debts.reduce((s, d) => s + parseFloat(d.current_balance || 0), 0)
  }, [debts])

  const weightedRate = useMemo(() => {
    if (totalBalance === 0) return 0
    const weighted = debts.reduce((s, d) =>
      s + (parseFloat(d.current_balance || 0) * parseFloat(d.interest_rate || 0)), 0)
    return weighted / totalBalance
  }, [debts, totalBalance])

  const strategyResult = useMemo(() => {
    if (debts.length === 0) return null
    const debtsForStrategy = debts.map(d => ({
      id: d.id,
      name: d.name,
      balance: parseFloat(d.current_balance || 0),
      rate: parseFloat(d.interest_rate || 0),
      minPayment: parseFloat(d.monthly_payment || 0) + (parseFloat(perDebtExtras[d.id]) || 0),
    }))
    return runStrategy(debtsForStrategy, totalBudget, strategy, overrides)
  }, [debts, totalBudget, strategy, perDebtExtras, overrides])

  const baselineResult = useMemo(() => {
    if (debts.length === 0) return null
    const debtsForBaseline = debts.map(d => ({
      id: d.id,
      name: d.name,
      balance: parseFloat(d.current_balance || 0),
      rate: parseFloat(d.interest_rate || 0),
      minPayment: parseFloat(d.monthly_payment || 0),
    }))
    return runStrategy(debtsForBaseline, totalMinPayment, 'avalanche')
  }, [debts, totalMinPayment])

  if (loading) {
    return <div style={styles.loading}>Loading debts…</div>
  }

  if (debts.length === 0) {
    return (
      <div style={styles.empty}>
        <h2 style={styles.emptyTitle}>No debts to track</h2>
        <p style={styles.emptyText}>
          Add a debt to see payoff strategies, interest projections, and payment history.
        </p>
        <button type="button" style={styles.emptyCta} onClick={() => setActiveForm('add')}>
          + Add debt
        </button>
        {activeForm && (
          <DebtForm
            debt={activeForm === 'add' ? null : activeForm.debt}
            partnerId={partnerId}
            onClose={() => setActiveForm(null)}
            onSaved={() => { setActiveForm(null); reloadDebts() }}
          />
        )}
      </div>
    )
  }

  const interestSaved = baselineResult && strategyResult && isFinite(baselineResult.totalInterest)
    ? baselineResult.totalInterest - strategyResult.totalInterest
    : 0

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>Debts</h1>
          <p style={styles.tagline}>
            Plan your payoff strategy and see how extra payments add up over time.
          </p>
        </div>
        <button type="button" style={styles.addDebtBtn} onClick={() => setActiveForm('add')}>
          + Add debt
        </button>
      </header>

      <section style={styles.summary}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Total Balance</div>
          <div style={styles.summaryValue}>{formatCurrency(totalBalance)}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Weighted Avg Rate</div>
          <div style={styles.summaryValue}>{weightedRate.toFixed(2)}%</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Min Monthly</div>
          <div style={styles.summaryValue}>{formatCurrency(totalMinPayment)}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Debt-free in</div>
          <div style={styles.summaryValue}>
            {strategyResult ? formatMonths(strategyResult.months) : '—'}
          </div>
          {strategyResult && isFinite(strategyResult.months) && (
            <div style={styles.summarySubvalue}>
              {formatDate(monthsFromNow(strategyResult.months))}
            </div>
          )}
        </div>
      </section>

      <section style={styles.strategySection}>
        <div style={styles.strategyHeader}>
          <h2 style={styles.sectionTitle}>Payoff Strategy</h2>
          <button
            type="button"
            style={styles.howLink}
            onClick={() => setShowHowItWorks(s => !s)}
          >
            {showHowItWorks ? 'Hide explanation' : 'How does this work?'}
          </button>
        </div>

        {showHowItWorks && (
          <div style={styles.howBox}>
            <p style={styles.howPara}>
              <strong>Avalanche:</strong> Pay extra on the debt with the
              highest interest rate first. Mathematically optimal — you pay
              the least total interest.
            </p>
            <p style={styles.howPara}>
              <strong>Snowball:</strong> Pay extra on the smallest balance
              first. You pay slightly more total interest, but you eliminate
              debts faster, which keeps motivation high.
            </p>
            <p style={styles.howDisclaimer}>
              Educational only. We're not financial advisors — these
              calculations don't account for tax implications, fees, or
              changes in your situation. Talk to a professional before
              making big financial decisions.
            </p>
          </div>
        )}

        <div style={styles.strategyToggle}>
          <button
            type="button"
            style={{
              ...styles.strategyBtn,
              ...(strategy === 'avalanche' ? styles.strategyBtnActive : {}),
            }}
            onClick={() => setStrategy('avalanche')}
          >
            🏔️ Avalanche
            <span style={styles.strategyHint}>Highest rate first</span>
          </button>
          <button
            type="button"
            style={{
              ...styles.strategyBtn,
              ...(strategy === 'snowball' ? styles.strategyBtnActive : {}),
            }}
            onClick={() => setStrategy('snowball')}
          >
            ⛄ Snowball
            <span style={styles.strategyHint}>Smallest balance first</span>
          </button>
        </div>

        <div style={styles.budgetCard}>
          <div style={styles.budgetLabel}>Extra monthly toward debt</div>
          <div style={styles.budgetMeta}>
            On top of {formatCurrency(totalMinPayment)} minimums
          </div>
          <div style={styles.budgetSliderRow}>
            <input
              type="range"
              min="0"
              max="2000"
              step="25"
              value={extraBudget}
              onChange={(e) => setExtraBudget(parseInt(e.target.value, 10))}
              style={styles.slider}
            />
            <div style={styles.budgetValue}>
              +{formatCurrency(extraBudget)}/mo
            </div>
          </div>
          <div style={styles.budgetSliderHints}>
            <span>$0</span><span>$500</span><span>$1,000</span><span>$1,500</span><span>$2,000</span>
          </div>
        </div>

        {strategyResult && (
          <div style={styles.outcomeRow}>
            <div style={styles.outcomeCard}>
              <div style={styles.outcomeLabel}>Total interest you'll pay</div>
              <div style={styles.outcomeValue}>
                {isFinite(strategyResult.totalInterest)
                  ? formatCurrency(strategyResult.totalInterest)
                  : '∞'}
              </div>
            </div>
            <div style={styles.outcomeCard}>
              <div style={styles.outcomeLabel}>Saved vs minimums</div>
              <div style={{
                ...styles.outcomeValue,
                color: interestSaved > 0 ? c.green : c.inkMuted,
              }}>
                {interestSaved > 0 ? formatCurrency(interestSaved) : '—'}
              </div>
            </div>
            <div style={styles.outcomeCard}>
              <div style={styles.outcomeLabel}>Time saved</div>
              <div style={styles.outcomeValue}>
                {baselineResult && isFinite(baselineResult.months) && isFinite(strategyResult.months)
                  ? formatMonths(baselineResult.months - strategyResult.months)
                  : '—'}
              </div>
            </div>
          </div>
        )}

        {strategyResult && !strategyResult.willPayOff && (
          <div style={styles.warningBox}>
            <span>Current payment doesn't cover the interest. Increase the budget to make progress.</span>
          </div>
        )}
      </section>

      {strategyResult && strategyResult.schedule.length > 0 && (
        <section style={styles.chartSection}>
          <h2 style={styles.sectionTitle}>Balance Over Time</h2>
          <BalanceChart
            schedule={strategyResult.schedule}
            debts={debts}
          />
        </section>
      )}

      {strategyResult && strategyResult.schedule.length > 0 && (
        <PayoffScheduleTable
          schedule={strategyResult.schedule}
          debts={debts}
          totalBudget={totalBudget}
          overrides={overrides}
          onOverride={handleOverride}
        />
      )}

      <section style={styles.debtsSection}>
        <h2 style={styles.sectionTitle}>Your Debts</h2>
        {debts.map(d => {
          const balance = parseFloat(d.current_balance || 0)
          const rate = parseFloat(d.interest_rate || 0)
          const min = parseFloat(d.monthly_payment || 0)
          const extra = parseFloat(perDebtExtras[d.id] || 0)
          const payoffMonth = strategyResult?.perDebtPayoff[d.id]
          const standalonePayoff = calculatePayoff(balance, rate, min + extra)
          return (
            <DebtCard
              key={d.id}
              debt={d}
              balance={balance}
              rate={rate}
              min={min}
              extra={extra}
              standalonePayoff={standalonePayoff}
              payoffMonthInStrategy={payoffMonth}
              accrual={accruals[d.id]}
              partnerId={partnerId}
              onExtraChange={(v) => setPerDebtExtras(prev => ({ ...prev, [d.id]: v }))}
              onEdit={() => setActiveForm({ debt: d })}
              onDelete={async () => {
                if (!window.confirm(`Delete "${d.name}"? This also removes its payment history and cannot be undone.`)) return
                await supabase.from('debts').delete().eq('id', d.id)
                emitDataEvent(EVENTS.DEBT_DELETED)
                reloadDebts()
              }}
            />
          )
        })}
      </section>

      {activeForm && (
        <DebtForm
          debt={activeForm === 'add' ? null : activeForm.debt}
          partnerId={partnerId}
          onClose={() => setActiveForm(null)}
          onSaved={() => { setActiveForm(null); reloadDebts() }}
        />
      )}

      <footer style={styles.footer}>
        <p style={styles.footerText}>
          These calculations are educational and don't account for fees,
          tax effects, or changes in your circumstances. Always consult a
          qualified financial advisor before major financial decisions.
        </p>
      </footer>
    </div>
  )
}

function DebtCard({ debt, balance, rate, min, extra, standalonePayoff, payoffMonthInStrategy, accrual, partnerId, onExtraChange, onEdit, onDelete }) {
  const [localExtra, setLocalExtra] = useState(extra)
  const [showHistory, setShowHistory] = useState(false)
  const tracked = debt.is_simple_mode === false

  useEffect(() => { setLocalExtra(extra) }, [extra])

  return (
    <div style={styles.debtCard}>
      <div style={styles.debtCardHeader}>
        <div>
          <div style={styles.debtName}>{debt.name}</div>
          <div style={styles.debtMeta}>
            {(debt.type || 'debt').replace(/_/g, ' ')} · {rate.toFixed(2)}% APR
            {tracked && <span style={styles.trackedTag}>tracking interest</span>}
          </div>
        </div>
        <div style={styles.debtHeaderRight}>
          <div style={styles.debtBalance}>
            {formatCurrency(balance)}
            <span style={styles.debtBalanceLabel}>balance</span>
          </div>
          <div style={styles.debtCardActions}>
            <button type="button" style={styles.debtCardActionBtn} onClick={onEdit}>Edit</button>
            <button type="button" style={{ ...styles.debtCardActionBtn, color: c.rose }} onClick={onDelete}>Delete</button>
          </div>
        </div>
      </div>

      {/* Interest accrued this session (No-Surprise Rule) — informational; the
          balance above already includes it. */}
      {accrual && accrual.amount > 0.005 && (
        <div style={styles.accrualLine}>
          Interest accrued since {formatShortDate(accrual.since)}: +{formatCurrencyDetailed(accrual.amount)}
        </div>
      )}

      <div style={styles.debtCardBody}>
        <div style={styles.debtStatGrid}>
          <div style={styles.debtStat}>
            <div style={styles.debtStatLabel}>Min payment</div>
            <div style={styles.debtStatValue}>{formatCurrencyDetailed(min)}</div>
          </div>
          <div style={styles.debtStat}>
            <div style={styles.debtStatLabel}>Extra to add</div>
            <input
              type="number"
              min="0"
              step="25"
              value={localExtra || ''}
              onChange={(e) => setLocalExtra(e.target.value)}
              onBlur={() => onExtraChange(parseFloat(localExtra) || 0)}
              placeholder="0"
              style={styles.debtExtraInput}
            />
          </div>
          <div style={styles.debtStat}>
            <div style={styles.debtStatLabel}>Solo payoff</div>
            <div style={styles.debtStatValue}>
              {standalonePayoff.willPayOff
                ? formatMonths(standalonePayoff.months)
                : 'never'}
            </div>
          </div>
          <div style={styles.debtStat}>
            <div style={styles.debtStatLabel}>In strategy</div>
            <div style={styles.debtStatValue}>
              {payoffMonthInStrategy
                ? formatDate(monthsFromNow(payoffMonthInStrategy))
                : '—'}
            </div>
          </div>
        </div>

        <button
          type="button"
          style={styles.historyToggle}
          onClick={() => setShowHistory(s => !s)}
        >
          {showHistory ? 'Hide payment history' : 'View payment history'}
        </button>
        {showHistory && <PaymentHistory debt={debt} partnerId={partnerId} />}
      </div>
    </div>
  )
}

// Inline reveal of a debt's payments (debt_payments joined to transactions).
// Running "balance after" is derived from the current balance walking backward
// (adding each payment's principal to reconstruct the older balance).
function PaymentHistory({ debt, partnerId }) {
  const [rows, setRows] = useState(null) // null = loading

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { data } = await supabase.from('debt_payments')
        .select('id, paid_at, principal_amount, interest_amount, transaction_id, transactions(merchant_name)')
        .eq('debt_id', debt.id)
        .order('paid_at', { ascending: false })
      if (cancelled) return
      // Derive "balance after" from the current balance, newest → oldest.
      let running = parseFloat(debt.current_balance || 0)
      const withBalance = (data || []).map(p => {
        const balanceAfter = running
        running += parseFloat(p.principal_amount || 0)
        return { ...p, balanceAfter }
      })
      setRows(withBalance)
    })()
    return () => { cancelled = true }
  }, [debt.id, debt.current_balance])

  if (rows === null) return <div style={styles.historyEmpty}>Loading…</div>
  if (rows.length === 0) return <div style={styles.historyEmpty}>No payment history yet.</div>

  return (
    <div style={styles.historyWrap}>
      <div style={{ ...styles.historyRow, ...styles.historyHead }}>
        <span style={styles.hDate}>Date</span>
        <span style={styles.hTxn}>Transaction</span>
        <span style={styles.hNum}>Principal</span>
        <span style={styles.hNum}>Interest</span>
        <span style={styles.hNum}>Balance after</span>
      </div>
      {rows.map(p => (
        <div key={p.id} style={styles.historyRow}>
          <span style={styles.hDate}>{formatShortDate(p.paid_at)}</span>
          <span style={styles.hTxn}>
            {p.transaction_id
              ? <Link to="/log" style={styles.hLink}>{p.transactions?.merchant_name || 'Payment'}</Link>
              : (p.transactions?.merchant_name || 'Payment')}
          </span>
          <span style={styles.hNum}>{formatCurrencyDetailed(parseFloat(p.principal_amount || 0))}</span>
          <span style={styles.hNum}>{formatCurrencyDetailed(parseFloat(p.interest_amount || 0))}</span>
          <span style={styles.hNum}>{formatCurrencyDetailed(p.balanceAfter)}</span>
        </div>
      ))}
    </div>
  )
}

function BalanceChart({ schedule, debts }) {
  const width = 720
  const height = 240
  const padding = { top: 10, right: 20, bottom: 30, left: 60 }
  const innerW = width - padding.left - padding.right
  const innerH = height - padding.top - padding.bottom

  const visibleSchedule = schedule.slice(0, 60)
  const maxMonth = visibleSchedule.length
  const maxBalance = Math.max(...visibleSchedule.map(s => s.totalBalance))

  if (maxMonth === 0 || maxBalance === 0) {
    return <div style={styles.chartEmpty}>No data to chart</div>
  }

  const xScale = (m) => padding.left + (m / maxMonth) * innerW
  const yScale = (v) => padding.top + (1 - v / maxBalance) * innerH

  const totalPath = visibleSchedule
    .map((s, i) => `${i === 0 ? 'M' : 'L'} ${xScale(s.month)} ${yScale(s.totalBalance)}`)
    .join(' ')

  const colors = ['#5B6CFF', '#FF6B5B', '#5BB76B', '#FFB45B', '#A55BFF']
  const debtPaths = debts.map((d, idx) => {
    const debtSchedule = visibleSchedule.map(s => {
      const p = s.payments.find(pp => pp.debtId === d.id)
      return { month: s.month, balance: p?.balance || 0 }
    })
    const path = debtSchedule
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(p.month)} ${yScale(p.balance)}`)
      .join(' ')
    return { id: d.id, name: d.name, path, color: colors[idx % colors.length] }
  })

  const yTicks = [0, maxBalance / 4, maxBalance / 2, (maxBalance * 3) / 4, maxBalance]

  return (
    <div style={styles.chart}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', maxWidth: width }}>
        {yTicks.map((tick, i) => (
          <g key={i}>
            <line
              x1={padding.left} y1={yScale(tick)}
              x2={width - padding.right} y2={yScale(tick)}
              stroke={c.line} strokeDasharray="2,3" strokeWidth="1"
            />
            <text
              x={padding.left - 8} y={yScale(tick)}
              textAnchor="end" dominantBaseline="middle"
              fontSize="11" fill={c.inkMuted}
            >
              {formatCurrency(tick)}
            </text>
          </g>
        ))}

        {[0, 12, 24, 36, 48, 60].filter(m => m <= maxMonth).map(m => (
          <text
            key={m}
            x={xScale(m)} y={height - 8}
            textAnchor="middle" fontSize="11" fill={c.inkMuted}
          >
            {m === 0 ? 'Now' : `${m}mo`}
          </text>
        ))}

        {debtPaths.map(d => (
          <path
            key={d.id}
            d={d.path}
            fill="none"
            stroke={d.color}
            strokeWidth="1.5"
            strokeOpacity="0.5"
          />
        ))}

        <path
          d={totalPath}
          fill="none"
          stroke={c.plum}
          strokeWidth="2.5"
        />
      </svg>

      <div style={styles.chartLegend}>
        <span style={styles.legendItem}>
          <span style={{ ...styles.legendDot, background: c.plum }} />
          Total
        </span>
        {debtPaths.map(d => (
          <span key={d.id} style={styles.legendItem}>
            <span style={{ ...styles.legendDot, background: d.color }} />
            {d.name}
          </span>
        ))}
      </div>
    </div>
  )
}

function PayoffScheduleTable({ schedule, debts, totalBudget, overrides, onOverride }) {
  const [expandFull, setExpandFull] = useState(false)
  const visibleMonths = expandFull ? schedule : schedule.slice(0, 6)
  const totalMonths = schedule.length

  const debtTotals = {}
  for (const d of debts) {
    debtTotals[d.id] = schedule.reduce((sum, mo) => {
      const p = mo.payments.find(pp => pp.debtId === d.id)
      return sum + (p?.payment || 0)
    }, 0)
  }

  return (
    <section style={styles.scheduleSection}>
      <div style={styles.scheduleHeader}>
        <h2 style={styles.sectionTitle}>Payoff Schedule</h2>
        <span style={styles.scheduleHint}>
          Showing {visibleMonths.length} of {totalMonths} months · click a cell to override
        </span>
      </div>

      <div style={styles.scheduleScroll}>
        <table style={styles.scheduleTable}>
          <thead>
            <tr>
              <th style={styles.scheduleThLabel}>Debt</th>
              {visibleMonths.map(mo => (
                <th key={mo.month} style={styles.scheduleTh}>
                  Mo {mo.month}
                </th>
              ))}
              <th style={styles.scheduleThTotal}>Total Paid</th>
            </tr>
          </thead>
          <tbody>
            {debts.map(d => (
              <tr key={d.id}>
                <td style={styles.scheduleTdLabel}>
                  <div style={styles.scheduleDebtName}>{d.name}</div>
                  <div style={styles.scheduleDebtMeta}>
                    {parseFloat(d.interest_rate || 0).toFixed(2)}% APR
                  </div>
                </td>
                {visibleMonths.map(mo => {
                  const p = mo.payments.find(pp => pp.debtId === d.id)
                  const payment = p?.payment || 0
                  const balance = p?.balance || 0
                  const isOverridden = overrides[mo.month]?.[d.id] !== undefined
                  const isPaid = payment === 0 && balance === 0
                  return (
                    <td key={mo.month} style={styles.scheduleCell}>
                      {isPaid ? (
                        <span style={styles.scheduleCellPaid}>✓</span>
                      ) : (
                        <input
                          type="number"
                          min="0"
                          step="25"
                          value={isOverridden ? overrides[mo.month][d.id] : ''}
                          placeholder={Math.round(payment).toString()}
                          onChange={(e) => onOverride(mo.month, d.id, e.target.value)}
                          style={{
                            ...styles.scheduleCellInput,
                            ...(isOverridden ? styles.scheduleCellInputOverridden : {}),
                          }}
                        />
                      )}
                    </td>
                  )
                })}
                <td style={styles.scheduleTdTotal}>
                  ${Math.round(debtTotals[d.id]).toLocaleString()}
                </td>
              </tr>
            ))}
            <tr style={styles.scheduleTotalRow}>
              <td style={styles.scheduleTdLabel}>
                <strong>Monthly Total</strong>
                <div style={styles.scheduleDebtMeta}>
                  Budget: ${Math.round(totalBudget).toLocaleString()}
                </div>
              </td>
              {visibleMonths.map(mo => {
                const sum = mo.payments.reduce((s, p) => s + p.payment, 0)
                return (
                  <td key={mo.month} style={styles.scheduleColTotal}>
                    ${Math.round(sum).toLocaleString()}
                  </td>
                )
              })}
              <td style={styles.scheduleTdTotal}>
                ${Math.round(Object.values(debtTotals).reduce((s, n) => s + n, 0)).toLocaleString()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {totalMonths > 6 && (
        <button
          type="button"
          onClick={() => setExpandFull(s => !s)}
          style={styles.scheduleToggle}
        >
          {expandFull ? '← Collapse to 6 months' : `View full ${totalMonths}-month schedule →`}
        </button>
      )}

      <div style={styles.scheduleNote}>
        Empty cells will follow your selected strategy. Type a number to override.
      </div>
    </section>
  )
}

// ─── Add / edit / delete debt (Chunk 6 — moved from Plan.js) ────────────────
function DebtForm({ debt, partnerId, onClose, onSaved }) {
  const isEdit = !!debt
  const [name, setName] = useState(debt?.name || '')
  const [type, setType] = useState(debt?.type || 'credit_card')
  const [currentBalance, setCurrentBalance] = useState(debt?.current_balance?.toString() || '')
  const [monthlyPayment, setMonthlyPayment] = useState(debt?.monthly_payment?.toString() || '')
  const [interestRate, setInterestRate] = useState(debt?.interest_rate?.toString() || '')
  const [trackInterest, setTrackInterest] = useState(isEdit ? debt.is_simple_mode === false : true)
  const [saving, setSaving] = useState(false)

  const canSave = !!name && !!currentBalance && !saving

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    const { data: { user } } = await supabase.auth.getUser()
    const data = {
      name, type,
      current_balance: parseFloat(currentBalance),
      monthly_payment: monthlyPayment ? parseFloat(monthlyPayment) : null,
      interest_rate: interestRate ? parseFloat(interestRate) : null,
      is_simple_mode: !trackInterest,
      updated_at: new Date().toISOString(),
    }
    if (isEdit) {
      await supabase.from('debts').update(data).eq('id', debt.id)
      // Record the balance change (only when it actually moved).
      if (Math.abs(parseFloat(currentBalance) - parseFloat(debt.current_balance || 0)) >= 0.005) {
        await recordDebtBalance(debt.id, parseFloat(currentBalance), 'manual_update')
      }
      emitDataEvent(EVENTS.DEBT_UPDATED)
    } else {
      const { data: created } = await supabase.from('debts').insert({ ...data, user_id: user.id }).select().single()
      if (created) await recordDebtBalance(created.id, parseFloat(currentBalance), 'creation', created.created_at)
      emitDataEvent(EVENTS.DEBT_SAVED)
      // Mirror Plan's DebtForm: create the "Debt Payments" bucket child line for
      // the current month's budget so the debt shows up in Plan immediately.
      if (created && monthlyPayment) {
        await ensureDebtPlanLine(user.id, partnerId, created.name, parseFloat(monthlyPayment))
      }
    }
    setSaving(false)
    onSaved()
  }

  const handleDelete = async () => {
    if (!isEdit) return
    if (!window.confirm(`Delete "${debt.name}"? This also removes its payment history and cannot be undone.`)) return
    await supabase.from('debts').delete().eq('id', debt.id)
    emitDataEvent(EVENTS.DEBT_DELETED)
    onSaved()
  }

  return (
    <div style={styles.formOverlay} onClick={(e) => { if (e.target === e.currentTarget) onClose() }} role="dialog" aria-modal="true">
      <div style={styles.formSheet}>
        <header style={styles.formHeader}>
          <span style={styles.formTitle}>{isEdit ? 'Edit debt' : 'Add debt'}</span>
          <button type="button" style={styles.formClose} onClick={onClose} aria-label="Close">×</button>
        </header>
        <div style={styles.formBody}>
          <label style={styles.formField}>
            <span style={styles.formLabel}>Name</span>
            <input style={styles.formInput} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Chase Sapphire" />
          </label>
          <label style={styles.formField}>
            <span style={styles.formLabel}>Type</span>
            <select style={styles.formInput} value={type} onChange={(e) => setType(e.target.value)}>
              {DEBT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          <label style={styles.formField}>
            <span style={styles.formLabel}>Current balance</span>
            <input style={styles.formInput} type="number" step="0.01" value={currentBalance} onChange={(e) => setCurrentBalance(e.target.value)} placeholder="0.00" />
          </label>
          <label style={styles.formField}>
            <span style={styles.formLabel}>Monthly payment</span>
            <input style={styles.formInput} type="number" step="0.01" value={monthlyPayment} onChange={(e) => setMonthlyPayment(e.target.value)} placeholder="0.00" />
          </label>
          <label style={styles.formField}>
            <span style={styles.formLabel}>Interest rate (APR %)</span>
            <input style={styles.formInput} type="number" step="0.01" value={interestRate} onChange={(e) => setInterestRate(e.target.value)} placeholder="e.g. 18.99" />
          </label>
          <label style={styles.trackRow}>
            <input type="checkbox" checked={trackInterest} onChange={(e) => setTrackInterest(e.target.checked)} />
            <span>
              <span style={styles.trackLabel}>Track interest automatically</span>
              <span style={styles.trackHelp}>
                We grow the balance by daily interest between visits. Turn off to update the balance yourself from statements.
              </span>
            </span>
          </label>
          <div style={styles.formFooter}>
            {isEdit && (
              <button type="button" style={styles.formDelete} onClick={handleDelete} disabled={saving}>Delete</button>
            )}
            <button type="button" style={{ ...styles.formSave, opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'not-allowed' }} onClick={handleSave} disabled={!canSave}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add debt'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Create the current-month "Debt Payments" bucket child line for a new debt so
// it appears in Plan immediately (mirrors the old Plan.js DebtForm behavior).
async function ensureDebtPlanLine(userId, partnerId, debtName, payment) {
  const now = new Date()
  const month = now.getMonth() + 1
  const year = now.getFullYear()
  const userIds = [userId, ...(partnerId ? [partnerId] : [])]
  const { data: budget } = await supabase.from('budgets').select('id')
    .in('user_id', userIds).eq('month', month).eq('year', year).eq('is_active', true)
    .order('created_at').limit(1).maybeSingle()
  if (!budget) return
  const DEBT_BUCKET = 'Debt Payments'
  let { data: bucket } = await supabase.from('budget_categories').select('id')
    .eq('budget_id', budget.id).is('parent_id', null).eq('name', DEBT_BUCKET).limit(1).maybeSingle()
  if (!bucket) {
    const { data: nb } = await supabase.from('budget_categories').insert({
      user_id: userId, budget_id: budget.id, name: DEBT_BUCKET, icon: '💳', limit_amount: 0, sort_order: 999,
    }).select('id').single()
    bucket = nb
  }
  if (!bucket) return
  const { data: existing } = await supabase.from('budget_categories').select('id')
    .eq('budget_id', budget.id).eq('parent_id', bucket.id).eq('name', debtName).limit(1).maybeSingle()
  if (existing) return
  await supabase.from('budget_categories').insert({
    user_id: userId, budget_id: budget.id, name: debtName, icon: '💳',
    limit_amount: payment, parent_id: bucket.id, sort_order: 999,
  })
  emitDataEvent(EVENTS.CATEGORY_SAVED)
}

const styles = {
  page: { padding: '24px', maxWidth: '1100px', margin: '0 auto' },
  loading: { padding: '40px', textAlign: 'center', color: c.inkMuted },
  empty: {
    padding: '60px 24px', textAlign: 'center',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
  },
  emptyTitle: { fontSize: '20px', fontWeight: 600, color: c.plum, margin: 0 },
  emptyText: { color: c.inkMuted, fontSize: '14px', maxWidth: '320px' },
  emptyCta: {
    marginTop: '6px', padding: '10px 18px', borderRadius: '10px',
    background: c.plum, color: c.cream, border: 'none',
    fontFamily: f.body, fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  },
  header: {
    marginBottom: '24px', display: 'flex', alignItems: 'flex-start',
    justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap',
  },
  headerLeft: { minWidth: 0 },
  addDebtBtn: {
    padding: '10px 16px', borderRadius: '10px', flexShrink: 0,
    background: c.plum, color: c.cream, border: 'none',
    fontFamily: f.body, fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  },
  title: {
    fontFamily: f.display, fontSize: '32px', fontWeight: 600,
    color: c.plum, margin: 0, letterSpacing: '-0.02em',
  },
  tagline: { color: c.inkMuted, fontSize: '14px', margin: '6px 0 0' },

  // Chunk 6 — card actions, accrual line, tracked tag, payment history
  debtHeaderRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' },
  debtCardActions: { display: 'flex', gap: '8px' },
  debtCardActionBtn: {
    background: 'transparent', border: 'none', padding: '2px 4px',
    fontFamily: f.body, fontSize: '12.5px', fontWeight: 600, color: c.plumSoft, cursor: 'pointer',
  },
  trackedTag: {
    marginLeft: '8px', padding: '1px 7px', borderRadius: '6px',
    background: c.greenSoft, color: c.green, fontSize: '10.5px', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  accrualLine: {
    padding: '8px 16px', background: c.cream2,
    borderTop: `1px solid ${c.line}`, borderBottom: `1px solid ${c.line}`,
    fontSize: '12.5px', color: c.plumSoft,
  },
  historyToggle: {
    marginTop: '14px', background: 'transparent', border: 'none',
    padding: '4px 0', fontFamily: f.body, fontSize: '13px', fontWeight: 600,
    color: c.green, cursor: 'pointer',
  },
  historyEmpty: { fontSize: '13px', color: c.inkMuted, padding: '8px 0' },
  historyWrap: { marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '2px', overflowX: 'auto' },
  historyRow: {
    display: 'grid', gridTemplateColumns: '84px 1fr 84px 74px 96px',
    gap: '8px', padding: '6px 0', fontSize: '12.5px', color: c.plum,
    borderBottom: `1px solid ${c.line}`, alignItems: 'center', minWidth: '420px',
  },
  historyHead: { color: c.inkMuted, fontWeight: 600, borderBottom: `1px solid ${c.lineStrong}` },
  hDate: {},
  hTxn: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  hNum: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  hLink: { color: c.green, textDecoration: 'none', fontWeight: 600 },

  // Chunk 6 — DebtForm modal
  formOverlay: {
    position: 'fixed', inset: 0, overscrollBehaviorX: 'contain', background: 'rgba(30, 16, 64, 0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px', zIndex: 1000,
  },
  formSheet: {
    overscrollBehaviorX: 'contain',
    background: c.white, borderRadius: '18px', width: '100%', maxWidth: '440px',
    maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(30,16,64,0.28)',
  },
  formHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 20px', borderBottom: `1px solid ${c.line}`,
  },
  formTitle: { fontFamily: f.display, fontSize: '18px', fontWeight: 600, color: c.plum },
  formClose: {
    background: 'transparent', border: 'none', fontSize: '22px', lineHeight: 1,
    color: c.inkMuted, cursor: 'pointer', padding: '0 4px',
  },
  formBody: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' },
  formField: { display: 'flex', flexDirection: 'column', gap: '6px' },
  formLabel: { fontSize: '13px', fontWeight: 600, color: c.plumSoft },
  formInput: {
    padding: '11px 12px', borderRadius: '10px', border: `1px solid ${c.lineStrong}`,
    fontFamily: f.body, fontSize: '15px', color: c.plum, background: c.white, width: '100%',
  },
  trackRow: {
    display: 'flex', gap: '10px', alignItems: 'flex-start',
    padding: '12px', background: c.cream2, borderRadius: '10px', cursor: 'pointer',
  },
  trackLabel: { display: 'block', fontSize: '14px', fontWeight: 600, color: c.plum },
  trackHelp: { display: 'block', fontSize: '12.5px', color: c.inkMuted, marginTop: '3px', lineHeight: 1.4 },
  formFooter: { display: 'flex', gap: '10px', marginTop: '4px', justifyContent: 'flex-end' },
  formDelete: {
    padding: '11px 16px', borderRadius: '10px', border: `1px solid ${c.lineStrong}`,
    background: 'transparent', color: c.rose, fontFamily: f.body, fontSize: '14px', fontWeight: 600, cursor: 'pointer',
    marginRight: 'auto',
  },
  formSave: {
    padding: '11px 20px', borderRadius: '10px', border: 'none',
    background: c.green, color: '#FFFFFF', fontFamily: f.body, fontSize: '14px', fontWeight: 600,
  },
  summary: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px', marginBottom: '32px',
  },
  summaryCard: {
    background: c.cream2, borderRadius: '12px', padding: '16px',
    border: `1px solid ${c.line}`,
  },
  summaryLabel: {
    fontSize: '11px', color: c.inkMuted, textTransform: 'uppercase',
    letterSpacing: '0.05em', fontWeight: 600, marginBottom: '6px',
  },
  summaryValue: {
    fontFamily: f.display, fontSize: '22px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.015em',
  },
  summarySubvalue: { fontSize: '12px', color: c.inkMuted, marginTop: '2px' },
  strategySection: {
    background: c.white, borderRadius: '16px', padding: '24px',
    marginBottom: '24px', border: `1px solid ${c.line}`,
  },
  strategyHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontFamily: f.display, fontSize: '20px', fontWeight: 600,
    color: c.plum, margin: 0, letterSpacing: '-0.015em',
  },
  howLink: {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    fontSize: '13px', color: c.green, fontWeight: 500,
    background: 'none', border: 'none', cursor: 'pointer',
  },
  howBox: {
    background: c.cream2, borderRadius: '10px', padding: '14px',
    marginBottom: '16px', border: `1px solid ${c.line}`,
  },
  howPara: { fontSize: '13.5px', color: c.ink, margin: '0 0 8px', lineHeight: 1.5 },
  howDisclaimer: {
    fontSize: '12.5px', color: c.inkMuted, marginTop: '12px',
    fontStyle: 'italic', borderTop: `1px dashed ${c.line}`, paddingTop: '10px',
  },
  strategyToggle: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px',
    marginBottom: '20px',
  },
  strategyBtn: {
    padding: '14px', borderRadius: '12px', border: `2px solid ${c.line}`,
    background: c.cream2, cursor: 'pointer', textAlign: 'center',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
    fontSize: '15px', fontWeight: 600, color: c.plum,
    transition: 'border 100ms, background 100ms',
  },
  strategyBtnActive: {
    border: `2px solid ${c.green}`, background: c.greenSoft,
  },
  strategyHint: {
    fontSize: '11.5px', color: c.inkMuted, fontWeight: 400,
  },
  budgetCard: {
    background: c.cream2, borderRadius: '10px', padding: '16px',
    border: `1px solid ${c.line}`, marginBottom: '16px',
  },
  budgetLabel: { fontSize: '14px', fontWeight: 600, color: c.plum },
  budgetMeta: { fontSize: '12px', color: c.inkMuted, marginBottom: '12px' },
  budgetSliderRow: {
    display: 'flex', alignItems: 'center', gap: '14px',
  },
  slider: { flex: 1, accentColor: c.green },
  budgetValue: {
    fontFamily: f.display, fontSize: '18px', fontWeight: 600,
    color: c.green, minWidth: '120px', textAlign: 'right',
  },
  budgetSliderHints: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: '10.5px', color: c.inkMuted, marginTop: '6px',
  },
  outcomeRow: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px', marginBottom: '8px',
  },
  outcomeCard: {
    background: c.cream2, borderRadius: '10px', padding: '12px',
    border: `1px solid ${c.line}`, textAlign: 'center',
  },
  outcomeLabel: {
    fontSize: '11px', color: c.inkMuted, textTransform: 'uppercase',
    letterSpacing: '0.04em', fontWeight: 600, marginBottom: '4px',
  },
  outcomeValue: {
    fontFamily: f.display, fontSize: '20px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.015em',
  },
  warningBox: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: 'rgba(244, 88, 88, 0.08)', color: '#d23',
    padding: '10px 14px', borderRadius: '10px', fontSize: '13px',
    marginTop: '12px',
  },
  chartSection: {
    background: c.white, borderRadius: '16px', padding: '24px',
    marginBottom: '24px', border: `1px solid ${c.line}`,
  },
  chart: { marginTop: '14px' },
  chartEmpty: { color: c.inkMuted, fontSize: '13px', padding: '20px', textAlign: 'center' },
  chartLegend: {
    display: 'flex', flexWrap: 'wrap', gap: '12px',
    marginTop: '12px', justifyContent: 'center',
  },
  legendItem: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    fontSize: '12px', color: c.ink,
  },
  legendDot: {
    width: '10px', height: '10px', borderRadius: '50%',
  },
  debtsSection: {
    background: c.white, borderRadius: '16px', padding: '24px',
    marginBottom: '24px', border: `1px solid ${c.line}`,
  },
  debtCard: {
    border: `1px solid ${c.line}`, borderRadius: '12px',
    marginBottom: '12px', overflow: 'hidden',
  },
  debtCardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 16px', background: c.cream2,
  },
  debtName: { fontFamily: f.display, fontSize: '16px', fontWeight: 600, color: c.plum },
  debtMeta: { fontSize: '12px', color: c.inkMuted, marginTop: '2px', textTransform: 'capitalize' },
  debtBalance: {
    fontFamily: f.display, fontSize: '20px', fontWeight: 600,
    color: c.plum, textAlign: 'right',
  },
  debtBalanceLabel: {
    display: 'block', fontSize: '11px', color: c.inkMuted,
    fontWeight: 400, fontFamily: 'inherit',
  },
  debtCardBody: { padding: '14px 16px' },
  debtStatGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px',
  },
  debtStat: {
    display: 'flex', flexDirection: 'column', gap: '4px',
  },
  debtStatLabel: {
    fontSize: '11px', color: c.inkMuted, textTransform: 'uppercase',
    letterSpacing: '0.04em', fontWeight: 600,
  },
  debtStatValue: {
    fontFamily: f.display, fontSize: '15px', fontWeight: 600, color: c.plum,
  },
  debtExtraInput: {
    fontFamily: f.display, fontSize: '15px', fontWeight: 600, color: c.green,
    background: c.cream2, border: `1px solid ${c.line}`,
    borderRadius: '6px', padding: '4px 8px', width: '90px',
    outline: 'none',
  },
  scheduleSection: {
    background: c.white, borderRadius: '16px', padding: '24px',
    marginBottom: '24px', border: `1px solid ${c.line}`,
  },
  scheduleHeader: {
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    marginBottom: '14px', flexWrap: 'wrap', gap: '8px',
  },
  scheduleHint: {
    fontSize: '12px', color: c.inkMuted, fontStyle: 'italic',
  },
  scheduleScroll: {
    overflowX: 'auto', marginBottom: '12px',
  },
  scheduleTable: {
    borderCollapse: 'collapse', width: '100%', minWidth: '600px',
    fontSize: '13px',
  },
  scheduleTh: {
    padding: '8px 6px', textAlign: 'center', fontSize: '11px',
    color: c.inkMuted, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.04em', borderBottom: `1px solid ${c.line}`,
    whiteSpace: 'nowrap', minWidth: '70px',
  },
  scheduleThLabel: {
    padding: '8px 12px 8px 0', textAlign: 'left', fontSize: '11px',
    color: c.inkMuted, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.04em', borderBottom: `1px solid ${c.line}`,
    minWidth: '140px',
  },
  scheduleThTotal: {
    padding: '8px 6px', textAlign: 'right', fontSize: '11px',
    color: c.inkMuted, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.04em', borderBottom: `1px solid ${c.line}`,
    minWidth: '90px',
  },
  scheduleTdLabel: {
    padding: '8px 12px 8px 0', borderBottom: `1px solid ${c.line}`,
  },
  scheduleDebtName: {
    fontFamily: f.display, fontSize: '14px', fontWeight: 600, color: c.plum,
  },
  scheduleDebtMeta: {
    fontSize: '11px', color: c.inkMuted, marginTop: '2px',
  },
  scheduleCell: {
    padding: '4px', borderBottom: `1px solid ${c.line}`,
    textAlign: 'center',
  },
  scheduleCellInput: {
    width: '60px', padding: '4px 6px', fontSize: '12px',
    fontFamily: f.display, fontWeight: 600, textAlign: 'center',
    border: `1px solid transparent`, borderRadius: '4px',
    background: 'transparent', color: c.plum, outline: 'none',
  },
  scheduleCellInputOverridden: {
    background: c.greenSoft, border: `1px solid ${c.green}`,
    color: c.green,
  },
  scheduleCellPaid: {
    color: c.green, fontWeight: 700, fontSize: '14px',
  },
  scheduleTotalRow: {
    background: c.cream2, fontWeight: 600,
  },
  scheduleColTotal: {
    padding: '8px 6px', textAlign: 'center', fontSize: '13px',
    fontFamily: f.display, fontWeight: 600, color: c.plum,
    borderTop: `2px solid ${c.line}`,
  },
  scheduleTdTotal: {
    padding: '8px 6px', textAlign: 'right', fontSize: '14px',
    fontFamily: f.display, fontWeight: 600, color: c.plum,
    borderBottom: `1px solid ${c.line}`,
  },
  scheduleToggle: {
    background: 'none', border: 'none', color: c.green,
    cursor: 'pointer', fontWeight: 500, fontSize: '13px',
    padding: '8px 0',
  },
  scheduleNote: {
    fontSize: '12px', color: c.inkMuted, fontStyle: 'italic',
    marginTop: '8px',
  },
}